import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, useTheme } from '@mui/material';
import { getAuth } from 'firebase/auth';
import { ModernStackedAreaChart } from './index';
import BASE_URL from '../../config';

/**
 * Modern Lake Status History Chart
 * Fetches and displays stacked area chart for lake infested/clean area over time
 */
function LakeStatusChart({ selectedSiteCode }) {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const user = getAuth().currentUser;
        if (!user) {
          console.error('No user logged in');
          setError('Please log in to view this chart');
          return;
        }

        const token = await user.getIdToken();

        // Create URL with params
        let url = `${BASE_URL}/api/status-history`;
        const params = new URLSearchParams();

        // Add site code parameter if provided
        if (selectedSiteCode) {
          params.append('site_id', selectedSiteCode);
        }

        // Append params to URL if there are any
        if (params.toString()) {
          url += `?${params.toString()}`;
        }

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch status history');
        }

        const jsonData = await response.json();

        // Transform data for recharts format
        const formattedData = jsonData
          .filter((row) => row.recorded_at != null)
          .map((row) => {
            const date = new Date(row.recorded_at);
            const infestedArea = Number(row.area_acre) || 0;
            const lakeArea = Number(row.water_acres) || 0; // total lake area, not "clean"
            return {
              date: date.toISOString().split('T')[0], // Format as YYYY-MM-DD
              timestamp: date.getTime(), // Keep timestamp for sorting
              infestationPct: lakeArea > 0 ? (infestedArea / lakeArea) * 100 : 0,
            };
          })
          .sort((a, b) => a.timestamp - b.timestamp); // Sort by date

        setData(formattedData);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load chart data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedSiteCode]);

  if (loading) {
    return (
      <Box sx={{ 
        height: 400, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <CircularProgress size={40} />
        <Typography sx={{ ml: 2, color: 'text.secondary' }}>
          Loading chart data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        height: 400, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <Typography sx={{ color: 'error.main' }}>
          {error}
        </Typography>
      </Box>
    );
  }

  const chartTitle = selectedSiteCode 
    ? `Lake Area Status for ${selectedSiteCode}` 
    : 'Lake Area Status Over Time';

  return (
    <ModernStackedAreaChart
      data={data}
      title={chartTitle}
      subtitle="Infestation (% of lake area)"
      height={350}
      xAxisKey="date"
      areas={[
        {
          dataKey: 'infestationPct',
          name: 'Infestation',
          color: theme.palette.error.main
        }
      ]}
      formatYAxis={(value) => `${value.toFixed(0)}%`}
    />
  );
}

export default LakeStatusChart;
